import classNames from 'classnames'
import { TextField } from 'formik-mui'
import DeleteIcon from 'mdi-react/DeleteIcon'
import React, { useCallback, useMemo } from 'react'
import { SelectFormik } from 'scenes/Dashboard/components/Inputs/Select'
import { getOrganizationEventTypes } from 'scenes/Dashboard/data/organizationEvents/helpers'
import { OrganizationEventType } from 'scenes/Dashboard/data/organizationEvents/types'
import { makeStyles } from 'tss-react/mui'
import { connect, FastField, FormProps } from 'utils-next/forms'

import { IconButton, Theme } from '@mui/material'

import { OrganizationFormValues } from '../../OrganizationDetailsEdit.models'
import {
  getOptionFromOrganizationEventType,
  getOrganizationEventTypeFromOption,
  OrganizationEventFormValue,
} from './OrganizationEventEdit.models'
import { buildOrganizationEvent } from './OrganizationEventsEditList.component'

type OrganizationEventEditOuterProps = {
  className: string
  index: number
  name: string
  organizationEventValue: OrganizationEventFormValue
  onClickDelete: () => void
}

type OrganizationEventEditProps = OrganizationEventEditOuterProps & {
  formik: FormProps<OrganizationFormValues>
}

const OrganizationEventEdit = React.memo(
  ({
    className,
    index,
    name,
    organizationEventValue,
    onClickDelete,
    formik: { isSubmitting, setFieldValue },
  }: OrganizationEventEditProps) => {
    const { classes } = useStyles()
    const namePrefix = `${name}[${index}]`
    const { options } = useOrganizationEventData(organizationEventValue)

    const onChangeType = useCallback(
      (newType: { value: OrganizationEventType }) => {
        setFieldValue(`${namePrefix}`, buildOrganizationEvent(newType.value))
      },
      [namePrefix, setFieldValue]
    )

    return (
      <div className={classNames(className, classes.container)}>
        <SelectFormik
          options={options}
          label="Type"
          name={`${namePrefix}.type`}
          disabled={isSubmitting}
          className={classes.selectField}
          onChange={onChangeType}
          autoFocus
        />

        <FastField
          component={TextField}
          label="Happened on"
          name={`${namePrefix}.happenedOn`}
          type="datetime-local"
          disabled={isSubmitting}
          className={classes.dateField}
          InputLabelProps={{ shrink: true }}
        />
        <IconButton aria-label="Delete Contact Attempt" className={classes.deleteButton} onClick={onClickDelete}>
          <DeleteIcon />
        </IconButton>
      </div>
    )
  }
)

OrganizationEventEdit.displayName = 'OrganizationEventEdit'

function getOrganizationEventTypeOptions() {
  return getOrganizationEventTypes().map(getOptionFromOrganizationEventType)
}

function useOrganizationEventData(organizationEventValue: OrganizationEventFormValue) {
  const type = getOrganizationEventTypeFromOption(organizationEventValue.type)
  const options = useMemo(getOrganizationEventTypeOptions, [])
  return { type, options }
}

const useStyles = makeStyles()(({ spacing }: Theme) => ({
  container: {
    display: 'flex',
    alignItems: 'flex-end',
  },
  selectField: {
    flexGrow: 3,
    minWidth: spacing(41),
    marginRight: spacing(1),
  },
  dateField: {
    flexGrow: 1,
    minWidth: spacing(19),
    marginRight: spacing(1),
  },
  deleteButton: {
    position: 'relative',
    top: spacing(1),
  },
}))

export default connect<OrganizationEventEditOuterProps, OrganizationFormValues>(OrganizationEventEdit)
